import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import type { ColumnFiltersState, PaginationState } from '@tanstack/react-table';
import { toast } from 'sonner';

import { apiAdminlogicTypeStepHistoriesGetCollection } from '@r/api/generated/client.ts';
import { unparse } from 'papaparse';
import { downloadCSV } from '@r/utils/utils.ts';
import type { LogicTypeStepHistoryLogicTypeStepHistoryCollectionReadModel } from '@r/api/generated/client.schemas.ts';
import { DataTable } from '@r/components/data-table/data-table.tsx';
import { DataTableSkeleton } from '@r/components/data-table/data-table-skeleton.tsx';
import { DataTableAdvancedToolbar } from '@r/components/data-table/advanced/data-table-advanced-toolbar.tsx';
import { useDataTable } from '@r/hooks/use-data-table.ts';
import { format as formatDate } from 'date-fns';
import {
  useStepsColumns,
  useStepsFilterFields,
  type StepCollection
} from '@admin/components/performances/steps-columns.tsx';
import { useUser } from '@r/api/auth.tsx';
import { getAdminTablePersistenceKey } from '@r/utils/data-table-persistence.ts';
import { isOrvalErrorFromPath } from '@r/api/orval-error-helper.ts';
import { useOrvalErrorToast } from '@r/hooks/use-orval-error-toast.ts';
import { useWorkspaceName } from '@r/hooks/use-workspace-name.ts';

type StepFilters = {
  companyName: string;
  companyUniqueCode: string;
  stepName: string;
  companyNameSortValue: 'asc' | 'desc' | undefined;
  stepNameSortValue: 'asc' | 'desc' | undefined;
  stepPointSortValue: 'asc' | 'desc' | undefined;
  createdAtSortValue: 'asc' | 'desc' | undefined;
};

type StepsQueryResult = {
  data: StepCollection[];
  pagination?: { last?: number; totalItems?: number };
};

export const Steps = () => {
  const { t } = useTranslation();
  const showOrvalErrorToast = useOrvalErrorToast();
  const [isExporting, setIsExporting] = useState(false);

  const authUser = useUser();
  const { sanitizedWorkspaceName } = useWorkspaceName();

  async function stepsListFiltersQuery(
    _filters: StepFilters,
    page: number | null = null,
    limit: number | null = null
  ): Promise<StepsQueryResult> {
    try {
      const {
        companyName,
        companyUniqueCode,
        stepName,
        companyNameSortValue,
        stepNameSortValue,
        stepPointSortValue,
        createdAtSortValue
      } = _filters;

      const queriesPayload = {
        page: page ?? pageIndex + 1,
        perPage: limit ?? pageSize,
        ...(companyName ? { 'company.name': companyName } : {}),
        ...(companyUniqueCode ? { 'company.uniqueCode': companyUniqueCode } : {}),
        ...(stepName ? { 'logicTypeStep.name': stepName } : {}),
        ...(companyNameSortValue ? { 'order[company.name]': companyNameSortValue } : {}),
        ...(stepNameSortValue ? { 'order[logicTypeStep.name]': stepNameSortValue } : {}),
        ...(stepPointSortValue ? { 'order[logicTypeStep.stepPoint]': stepPointSortValue } : {}),
        ...(createdAtSortValue ? { 'order[createdAt]': createdAtSortValue } : {})
      };

      const response = await apiAdminlogicTypeStepHistoriesGetCollection(queriesPayload);

      type ApiStep = LogicTypeStepHistoryLogicTypeStepHistoryCollectionReadModel;
      const mappedData: StepCollection[] =
        (response?.data as ApiStep[] | undefined)?.map((s) => ({
          id: s.id,
          company: {
            name: s.company?.name ?? null,
            uniqueCode: s.company?.uniqueCode ?? null
          },
          logicTypeStep: {
            name: s.logicTypeStep?.name ?? null,
            description: s.logicTypeStep?.description ?? null,
            stepPoint: s.logicTypeStep?.stepPoint ?? null
          },
          createdAt: s.createdAt ?? null
        })) ?? [];

      return {
        data: mappedData,
        pagination: response?.pagination
      };
    } catch (error) {
      if (isOrvalErrorFromPath(error, 'get', '/admin/steps')) {
        showOrvalErrorToast(error, t('toast.error.label'));
      } else {
        toast.error(`${t('toast.error.label')}`);
      }
      throw error;
    }
  }

  function stepsListFilters(): StepFilters {
    const companyName =
      (columnFilters.find((filter) => filter.id === 'companyName')?.value as string) ?? '';
    const companyUniqueCode =
      (columnFilters.find((filter) => filter.id === 'companyUniqueCode')?.value as string) ?? '';
    const stepNameFilter = columnFilters.find((filter) => filter.id === 'stepName')?.value;
    const stepName = Array.isArray(stepNameFilter)
      ? ((stepNameFilter[0] as string) ?? '')
      : ((stepNameFilter as string) ?? '');

    const sortValue = (id: string): 'asc' | 'desc' | undefined => {
      const sort = sorting.find((s) => s.id === id);
      return sort ? (sort.desc ? 'desc' : 'asc') : undefined;
    };

    // Tri par defaut : obtentions les plus recentes en haut, tant qu'aucun tri n'est actif.
    let createdAtSortValue = sortValue('createdAt');
    if (!createdAtSortValue && sorting.length === 0) {
      createdAtSortValue = 'desc';
    }

    return {
      companyName,
      companyUniqueCode,
      stepName,
      companyNameSortValue: sortValue('companyName'),
      stepNameSortValue: sortValue('stepName'),
      stepPointSortValue: sortValue('stepPoint'),
      createdAtSortValue
    };
  }

  function getStepDataForExport(step: StepCollection) {
    const sanitize = (value: string | number | undefined | null) =>
      String(value ?? '').replace(/[\r\n\t]/g, '');
    const formatForExportDate = (value?: string | null) =>
      value ? formatDate(new Date(value), 'yyyy-MM-dd') : '';

    return [
      sanitize(step.company?.name),
      sanitize(step.company?.uniqueCode),
      sanitize(step.logicTypeStep?.name),
      sanitize(step.logicTypeStep?.description),
      sanitize(step.logicTypeStep?.stepPoint),
      sanitize(formatForExportDate(step.createdAt))
    ];
  }

  const stepsListQuery = useQuery<StepsQueryResult>({
    queryKey: ['steps_list'],
    queryFn: async (): Promise<StepsQueryResult> => {
      return await stepsListFiltersQuery(stepsListFilters());
    }
  });

  const filterFields = useStepsFilterFields();

  const stepsTablePersistenceKey = useMemo(
    // Cle de persistance isolee par utilisateur pour conserver ses preferences.
    () => getAdminTablePersistenceKey('steps', authUser.data?.id),
    [authUser.data?.id]
  );

  const { table } = useDataTable<StepCollection, unknown>({
    data: stepsListQuery.data?.data ?? [],
    columns: useStepsColumns(),
    filterFields,
    pageCount: stepsListQuery.data?.pagination?.last ?? 1,
    enableAdvancedFilter: true,
    persistence: {
      // Persiste pagination, tri et filtres en localStorage.
      key: stepsTablePersistenceKey
    }
  });

  const { pageIndex, pageSize }: PaginationState = table.getState().pagination as PaginationState;
  const sorting = table.getState().sorting;
  const columnFilters: ColumnFiltersState = table.getState().columnFilters;
  const prevValuesRef = useRef({ pageIndex, pageSize, columnFilters, sorting });

  async function exportData() {
    setIsExporting(true);

    let allData: StepCollection[] = [];
    const limit = 500;

    const totalPageCount = Math.ceil((stepsListQuery.data?.pagination?.totalItems ?? 1) / limit);
    const promises = Array.from({ length: totalPageCount }, (_, i) =>
      stepsListFiltersQuery(stepsListFilters(), i + 1, limit)
    );

    const pagesData = await Promise.all(promises);
    allData = pagesData.flatMap((pageData) => pageData.data);

    const fields = [
      t('label.company.name'),
      t('label.company.unique.code'),
      t('label.step.name'),
      t('label.step.description'),
      t('label.step.point'),
      t('label.step.reached.at')
    ];

    const csv = unparse({
      fields: fields,
      data: allData.map((s) => getStepDataForExport(s))
    });

    const currentDate = new Date();
    const baseFileName = `steps-export-${currentDate.toISOString().slice(0, 19).replace(/[:T]/g, '-')}.csv`;
    const fileName = sanitizedWorkspaceName
      ? `${sanitizedWorkspaceName}_${baseFileName}`
      : baseFileName;
    downloadCSV(csv, fileName);

    setIsExporting(false);
  }

  useEffect(() => {
    const prevValues = prevValuesRef.current;
    if (
      pageIndex != prevValues.pageIndex ||
      pageSize != prevValues.pageSize ||
      JSON.stringify(columnFilters) !== JSON.stringify(prevValues.columnFilters) ||
      JSON.stringify(sorting) != JSON.stringify(prevValues.sorting)
    ) {
      stepsListQuery.refetch();
    }

    prevValuesRef.current = { pageIndex, pageSize, columnFilters, sorting };
  }, [pageIndex, pageSize, columnFilters, sorting]);

  return (
    <>
      <span className="text-4xl font-bold">{t('admin.steps.title')}</span>

      {stepsListQuery.isLoading && (
        <DataTableSkeleton columnCount={6} cellWidths={['24rem', '12rem', '12rem', '8rem']} />
      )}

      {stepsListQuery.isSuccess && (
        <>
          <DataTableAdvancedToolbar
            table={table}
            filterFields={filterFields}
            onExport={exportData}
            isExporting={isExporting}
          />

          <DataTable table={table} />
        </>
      )}
    </>
  );
};
